import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, ButtonType, ButtonVariant, Input } from 'components/core';
import { DashboardCopy } from 'helpers/dashboardCopy';
import { dashboardIdSchema, dashboardTitleSchema } from 'helpers/dashboardValidation';

interface SaveAsNewPanelProps {
  /** What the copy starts out as; both are editable. */
  initialTitle: string;
  initialId: string;
  /** Every dashboard that exists, the one being copied included, so the new ID cannot clash with any of them. */
  dashboards: { dashboardId?: string }[];
  titleMaxLength: number;
  /** A save is running: both buttons are disabled so the copy cannot be created twice. */
  busy?: boolean;
  onConfirm: (copy: DashboardCopy) => void;
  onCancel: () => void;
}

// Asks what to call the copy before it is saved. It sits above the editor form (not inside it), like the starter-template warning.
export const SaveAsNewPanel = ({ initialTitle, initialId, dashboards, titleMaxLength, busy = false, onConfirm, onCancel }: SaveAsNewPanelProps) => {
  const { t } = useTranslation();
  const schema = useMemo(
    () => Yup.object({ title: dashboardTitleSchema(t, titleMaxLength).trim(), dashboardId: dashboardIdSchema(dashboards, t).trim() }),
    [t, titleMaxLength, dashboards]
  );
  const { register, handleSubmit, formState } = useForm<DashboardCopy>({
    resolver: yupResolver(schema),
    defaultValues: { title: initialTitle, dashboardId: initialId },
  });
  const { errors } = formState;

  return (
    <section className="alert alert-info mx-3 mt-3" aria-label={t('DASHBOARD_COPY.HEADING')}>
      <div className="fw-bold mb-1">{t('DASHBOARD_COPY.HEADING')}</div>
      <p className="small mb-3">{t('DASHBOARD_COPY.NOTE')}</p>
      {([['title', 'TITLE_LABEL'], ['dashboardId', 'ID_LABEL']] as const).map(([field, labelKey]) => (
        <div className="mb-3" key={field}>
          <div className="small fw-semibold mb-1">{t(`DASHBOARD_COPY.${labelKey}`)}</div>
          <Input
            showTitle={false}
            showDescription={false}
            aria-label={t(`DASHBOARD_COPY.${labelKey}`)}
            className={errors[field] ? 'is-invalid' : ''}
            {...register(field)}
          />
          {errors[field] && <div className="text-danger small mt-1" role="alert">{errors[field]?.message}</div>}
        </div>
      ))}
      <div className="d-flex gap-2">
        <Button htmlType="button" size={ButtonSize.small} type={ButtonType.secondary} disabled={busy} onClick={handleSubmit((copy) => onConfirm(copy))}>
          {t('DASHBOARD_COPY.CONFIRM')}
        </Button>
        <Button htmlType="button" size={ButtonSize.small} type={ButtonType.secondary} variant={ButtonVariant.outline} disabled={busy} onClick={onCancel}>
          {t('ACTION.CANCEL')}
        </Button>
      </div>
    </section>
  );
};
